/**
 * The Plot tab's action row, pinned to the bottom of the sidebar.
 *
 * It lives outside `<plot-controls>` and directly under `.panel-left` on
 * purpose: a `position: sticky` element only stays put while its containing
 * block is on screen, so pinning it from inside the Plot card would let it
 * scroll away with that card. As the last child of the sidebar's flex column,
 * an auto top margin also pushes it to the bottom when the controls are shorter
 * than the sidebar. With overflowing controls the sticky position keeps it
 * reachable no matter how far down the user has scrolled.
 *
 * The button is disabled whenever pressing it would change nothing, and always
 * explains why on hover - see `plotBlockedReason`.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import {
  activeTab,
  activeSection,
  activeDataSource,
  selectedShip,
  selectedCols,
  allColumns,
  derivedSignals,
  startDate,
  endDate,
  filters,
  filterLogic,
  filterExpr,
  filterMode,
  outliers,
  comparisonShips,
  plottedShipIds,
} from '../../state/store.ts';
import { plotState, isPlotEnabled, type PlotState } from '../../state/plot-state.ts';
import { doPlot } from './plot-actions.ts';
import { buildPlotRequest, plotRequestSignature } from './plot-request.ts';
import {
  plotFetching,
  plotEta,
  plottedSignature,
  lastPlotData,
} from './plot-ui-state.ts';
import { sourceHasHistory } from '../../state/data-sources.ts';
import { scheduleEstimate } from '../../state/estimate.ts';
import { syncToUrl } from '../../state/url-sync.ts';
import type { PlotType } from '../../api/types.ts';
import {
  comparisonIssues,
  comparisonReady,
  periodComparison,
  stopPeriodComparison,
} from '../../state/period-comparison.ts';
import { periodRequestSignature } from './period-plot.ts';
import { runtime } from './plot-runtime.ts';
import { validateNumberFormula } from './number-formula.ts';
import { dialSignals } from './gauge-config.ts';
import { nodeTitle, sankeyProblem, sankeySignals } from './sankey-config.ts';
import { plotColumnPool } from '../ship/ship-columns.ts';
import { isNumericType } from '../../utils/columns.ts';

/** What each plot type still needs before it can be plotted (tooltip text). */
const MISSING_INPUT_HINT: Record<PlotType, string> = {
  line: 'Pick at least one Y-axis series.',
  scatter: 'Pick an X column and at least one Y-axis series.',
  histogram: 'Pick at least one column to bin.',
  heatmap: 'Pick an X and a Y column to bin against each other.',
  correlation: 'Pick at least two numeric signals to measure against each other.',
  pie: 'Pick two or more signals to share out, or one signal to cut into bands.',
  box: 'Pick at least one signal, or one signal and a column to split it by.',
  number: 'Pick a numeric signal or enter a calculation.',
  gauge: 'Give every dial a numeric signal or a calculation.',
  sankey: 'Give every Signal and Calculation node what it reads, and join the nodes as the note under the editor says.',
  table: 'Add at least one column, and give every column and group what it needs.',
  map: 'Pick a latitude and a longitude column.',
};

@customElement('plot-action-bar')
export class PlotActionBar extends LightElement {
  override connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this.onPlotShortcut);
  }

  override disconnectedCallback(): void {
    document.removeEventListener('keydown', this.onPlotShortcut);
    super.disconnectedCallback();
  }

  private onPlotShortcut = (e: KeyboardEvent): void => {
    if (e.key !== 'Enter' || !e.ctrlKey || e.altKey || e.shiftKey || e.metaKey
      || e.repeat || e.isComposing || e.defaultPrevented) return;
    if (activeSection.value !== 'explore' || activeTab.value !== 'plot') return;
    const target = e.target instanceof Element ? e.target : null;
    if (target?.closest('assistant-panel, dialog, [role="dialog"]')) return;
    if (document.querySelector('dialog[open], [role="dialog"][aria-modal="true"]')) return;
    const button = this.querySelector<HTMLButtonElement>('#plot-btn');
    if (!button || button.disabled || !this.getClientRects().length) return;
    e.preventDefault();
    button.click();
  };

  private store = new StoreController(this, [
    plotState,
    selectedShip,
    allColumns,
    derivedSignals,
    activeDataSource,
    plotFetching,
    plotEta,
    lastPlotData,
    // The Plot button compares the request it would send against the one that
    // drew the current chart, so it must re-render when any request input moves.
    plottedSignature,
    startDate,
    endDate,
    filters,
    filterLogic,
    filterExpr,
    filterMode,
    outliers,
    comparisonShips,
    periodComparison,
  ]);

  /**
   * Why the Plot button is unavailable, or null when it is clickable. The string
   * is the button's tooltip, so a greyed-out button always explains itself
   * instead of just looking broken.
   */
  private plotBlockedReason(s: PlotState): string | null {
    if (plotFetching.value) return null; // the label already reads "Loading…"
    if (!selectedShip.value) return 'Choose a ship first.';
    if (s.plot_type === 'number' && s.number_mode === 'formula') {
      const columns = plotColumnPool().filter((col) => col.derived || isNumericType(col.type));
      const error = validateNumberFormula(s.number_expression, (name) => columns.some((col) => col.name === name));
      if (error) return error;
    }
    if (s.plot_type === 'gauge') {
      const columns = plotColumnPool().filter((col) => col.derived || isNumericType(col.type));
      for (const d of s.gauge_dials) {
        if (d.mode !== 'formula' || !d.expression.trim()) continue;
        const error = validateNumberFormula(d.expression, (name) => columns.some((col) => col.name === name));
        if (error) return `${d.label.trim() || 'A dial'}: ${error}`;
      }
    }
    if (s.plot_type === 'sankey') {
      const columns = plotColumnPool().filter((col) => col.derived || isNumericType(col.type));
      for (const n of s.sankey_nodes) {
        if (n.kind !== 'formula' || !n.expression.trim()) continue;
        const error = validateNumberFormula(n.expression, (name) => columns.some((col) => col.name === name));
        if (error) return `${nodeTitle(n)}: ${error}`;
      }
      const problem = sankeyProblem(s.sankey_nodes);
      if (problem) return problem;
    }
    if (!isPlotEnabled(s)) return MISSING_INPUT_HINT[s.plot_type];
    if (periodComparison.value) {
      const issue = comparisonIssues()[0];
      if (issue) return issue.message;
    }
    // A rolling-window source has new rows by the next press, so an unchanged
    // configuration is still a meaningful request there.
    if (!sourceHasHistory(activeDataSource.value)) return null;
    const drawn = plottedSignature.value;
    const current = periodComparison.value
      ? periodRequestSignature()
      : plotRequestSignature(buildPlotRequest().body, plottedShipIds());
    if (drawn !== null && drawn === current) {
      return 'This plot is already showing - change a setting to plot again.';
    }
    return null;
  }

  /** Plot → Export: copy the plotted Y series into the column selection. */
  private exportPlottedSeries(): void {
    const s = plotState.value;
    const numberData = lastPlotData.value;
    const picks = [...new Set(s.plot_type === 'number' && numberData?.plot_type === 'number'
      ? Object.keys(numberData.valid_counts)
      : s.plot_type === 'gauge' ? dialSignals(s.gauge_dials)
        : s.plot_type === 'sankey' ? sankeySignals(s.sankey_nodes) : [...s.y_left, ...s.y_right])];
    if (picks.length === 0) return;
    selectedCols.set(new Set(picks));
    if (periodComparison.value) {
      stopPeriodComparison('Exporting period A. Return to Plot to restore the comparison.');
    }
    scheduleEstimate();
    activeTab.set('export');
    document.body.dataset.activeTab = 'export';
    syncToUrl();
  }

  override render(): TemplateResult {
    const s = plotState.value;
    // Export reads EOS only; with another source there is nothing to hand over.
    const canExport = sourceHasHistory(activeDataSource.value);
    const blocked = this.plotBlockedReason(s);
    const comparing = !!periodComparison.value;
    // "Update" only once a comparison is actually on screen. With A alone the
    // chart is still A's ordinary plot, and nothing about it is out of date.
    const showingComparison = comparing && comparisonReady() && !!runtime.lastByPeriod;
    const updateComparison = showingComparison && plottedSignature.value !== null;
    return html`
      <div class="plot-actions">
        ${!canExport
          ? nothing
          : html`<button
              class="btn btn-secondary"
              ?disabled=${s.plot_type === 'number'
                ? lastPlotData.value?.plot_type !== 'number' || !Object.keys(lastPlotData.value.valid_counts).length
                : s.plot_type === 'gauge'
                  ? !dialSignals(s.gauge_dials).length
                  : s.plot_type === 'sankey'
                    ? !sankeySignals(s.sankey_nodes).length
                    : s.y_left.length + s.y_right.length === 0}
              @click=${() => this.exportPlottedSeries()}
            >
              ${comparing ? 'Export period A ›' : 'Export selection ›'}
            </button>`}
        <!-- The wrapper keeps disabled-button explanations reachable. -->
        <span class="plot-btn-wrap" data-tour="plot-button" title=${blocked ?? 'Plot (Ctrl+Enter)'}>
          <button id="plot-btn" class="btn btn-primary" aria-keyshortcuts="Control+Enter"
            ?disabled=${blocked !== null || plotFetching.value}
            aria-busy=${plotFetching.value ? 'true' : 'false'}
            aria-label=${plotFetching.value ? `Loading ${plotEta.value ?? ''}`.trim() : nothing}
            @click=${() => void doPlot()}>
            <!-- Every state sits in one grid cell, the others hidden, so the
                 button is as wide as its widest state and never changes size. -->
            <span class="plot-btn-stack">
              <span class=${plotFetching.value ? 'is-off' : ''}>
                ${comparing ? updateComparison ? 'Update comparison' : 'Plot comparison' : 'Plot'}
              </span>
              <span class="plot-btn-busy ${plotFetching.value ? '' : 'is-off'}">
                <span class="spinner" aria-hidden="true"></span>
                ${plotEta.value ? html`<span class="plot-loading-eta">${plotEta.value}</span>` : nothing}
              </span>
              <span class="plot-btn-busy is-off" aria-hidden="true">
                <span class="spinner" aria-hidden="true"></span><span class="plot-loading-eta">~00s</span>
              </span>
            </span>
          </button>
        </span>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'plot-action-bar': PlotActionBar;
  }
}
